import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api, fmtPrice, BACKEND_URL } from "../lib/api";
import { useBusiness } from "../context/BusinessContext";
import { toast } from "sonner";
import { CheckCircle2, Clock, XCircle, Upload } from "lucide-react";

export default function OrderSuccess() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const orderId = id || params.get("order_id");
  const [order, setOrder] = useState(null);
  const { business } = useBusiness();
  const [uploading, setUploading] = useState(false);

  const refresh = () => {
    if (!orderId) return;
    api.get(`/orders/${orderId}`).then((r) => setOrder(r.data));
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  if (!orderId) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <h1 className="text-4xl font-black tracking-tighter">Order not found</h1>
        <Link to="/shop" className="mt-6 inline-block bg-black text-white px-8 py-4 text-xs uppercase tracking-ultra">
          Back to shop
        </Link>
      </div>
    );
  }

  if (!order) {
    return <div className="py-24 text-center text-sm">Loading order...</div>;
  }

  const uploadReceipt = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await fetch(`${BACKEND_URL}/api/payments/qr/upload?order_id=${order.id}`, {
        method: "POST",
        body: fd,
      }).then((r) => {
        if (!r.ok) throw new Error("Upload failed");
      });
      toast.success("Receipt uploaded. Awaiting admin verification.");
      refresh();
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const statusBadge = () => {
    if (order.payment_status === "paid") {
      return (
        <span className="inline-flex items-center gap-2 bg-black text-white px-3 py-1 text-[10px] uppercase tracking-ultra">
          <CheckCircle2 className="w-3 h-3" /> Paid
        </span>
      );
    }
    if (order.payment_status === "failed") {
      return (
        <span className="inline-flex items-center gap-2 bg-accent text-white px-3 py-1 text-[10px] uppercase tracking-ultra">
          <XCircle className="w-3 h-3" /> Failed
        </span>
      );
    }
    if (order.payment_status === "pending_verification") {
      return (
        <span className="inline-flex items-center gap-2 bg-neutral-200 text-neutral-700 px-3 py-1 text-[10px] uppercase tracking-ultra">
          <Clock className="w-3 h-3" /> Awaiting verification
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-2 bg-neutral-200 text-neutral-700 px-3 py-1 text-[10px] uppercase tracking-ultra">
        <Clock className="w-3 h-3" /> Pending
      </span>
    );
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="order-page">
      <div className="text-xs uppercase tracking-ultra text-neutral-500">Order</div>
      <h1 className="mt-2 text-4xl md:text-6xl font-black tracking-tighter">
        {order.payment_status === "paid" ? "Thank you." : "Order received."}
      </h1>
      <div className="mt-6 flex flex-wrap items-center gap-3 text-sm">
        <span className="font-mono text-neutral-500">#{order.id.slice(0, 8).toUpperCase()}</span>
        <span>·</span>
        {statusBadge()}
      </div>

      <div className="mt-10 border border-black/10 bg-white p-6">
        <div className="text-xs uppercase tracking-ultra text-neutral-500 mb-4">Items</div>
        <div className="divide-y divide-black/10">
          {order.items.map((it) => (
            <div key={it.product_id} className="flex items-center gap-4 py-3">
              <div className="w-14 h-14 bg-neutral-200 overflow-hidden">
                {it.image_url && <img src={it.image_url} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1">
                <div className="text-sm font-medium">{it.name}</div>
                <div className="text-xs text-neutral-500">×{it.quantity}</div>
              </div>
              <div className="text-sm font-medium">
                {fmtPrice(it.price * it.quantity, business.currency)}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 pt-4 border-t border-black flex justify-between text-lg font-black">
          <span>Total</span>
          <span>{fmtPrice(order.total_amount, business.currency)}</span>
        </div>
      </div>

      {/* QR Payment flow */}
      {order.payment_method === "qr" && order.payment_status !== "paid" && (
        <div className="mt-10 border border-black bg-white p-6" data-testid="qr-payment-section">
          <div className="text-xs uppercase tracking-ultra text-neutral-500 mb-4">
            QR payment
          </div>
          <div className="grid md:grid-cols-2 gap-6 items-start">
            <div className="aspect-square bg-[#f0f0ea] overflow-hidden">
              {business.qr_payment_image ? (
                <img src={business.qr_payment_image} alt="QR" className="w-full h-full object-contain" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-xs text-neutral-500">
                  Admin has not uploaded a QR yet.
                </div>
              )}
            </div>
            <div>
              <p className="text-sm text-neutral-700 leading-relaxed">
                Scan the QR and pay <span className="font-bold">{fmtPrice(order.total_amount, business.currency)}</span>. Upload the receipt below. Our team will verify and update your order.
              </p>
              {order.qr_receipt_image ? (
                <div className="mt-4">
                  <div className="text-[10px] uppercase tracking-ultra text-neutral-500 mb-2">Uploaded receipt</div>
                  <img src={order.qr_receipt_image} alt="Receipt" className="border border-black/10 max-h-60" />
                </div>
              ) : (
                <label className="mt-6 inline-flex items-center gap-2 cursor-pointer bg-black text-white px-6 py-3 text-xs uppercase tracking-ultra font-bold hover:bg-neutral-800" data-testid="qr-upload-label">
                  <Upload className="w-4 h-4" />
                  {uploading ? "Uploading..." : "Upload receipt"}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={uploadReceipt}
                    data-testid="qr-upload-input"
                  />
                </label>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="mt-10">
        <Link to="/shop" className="text-xs uppercase tracking-ultra underline underline-offset-4" data-testid="order-continue-link">
          Continue shopping →
        </Link>
      </div>
    </div>
  );
}
